import { useState } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  QualityIssueDetailSchema,
  type EmployeeDto,
  type QualityActionDto,
  type QualityIssueDetailDto,
} from '@taqon/contracts';
import { Link as RouterLink, useParams } from 'react-router';
import { getData, patchJson, postJson } from '../../shared/api/client';
import { RecordHistory } from '../../shared/audit/AuditTrail';
import { DocumentsPanel } from '../../shared/documents/DocumentsPanel';
import { hasCapability, useSession } from '../../shared/session/session';
import { FormDialog, StatusChip } from '../../shared/ui/forms';
import { EmptyState, LoadingState, PageHeader, Section } from '../../shared/ui/page';
import { PersonName } from '../../shared/ui/person';
import { ActiveEmployeePicker, ConfirmDialog } from '../crm/common';
import { Facts, RecordFailure } from '../finance/detail';
import { ACTION_TYPE_LABELS, listLabel, QUALITY_ISSUES, useQualityLists } from './common';
import { QualityIssueDialog } from './QualityIssuesPage';

type Dialog =
  | { kind: 'edit' }
  | { kind: 'close' }
  | { kind: 'action'; action?: QualityActionDto }
  | { kind: 'complete'; action: QualityActionDto };

/**
 * One quality issue (`/quality/issues/:id`; FRD-QLT-003 to QLT-008): what it is, its owner and
 * root cause, its corrective and preventive actions, and, once closed, the verification. Editing
 * and actions need the change permission; verifying and closing needs its own.
 */
export function QualityIssueDetailPage() {
  const { id = '' } = useParams();
  const session = useSession();
  const lists = useQualityLists();
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const detail = useQuery({
    queryKey: ['quality', 'issues', 'one', id],
    queryFn: () => getData(`${QUALITY_ISSUES}/${id}`, QualityIssueDetailSchema),
  });
  const back = (
    <Button component={RouterLink} to="/quality/issues" startIcon={<ArrowBackIcon />}>
      All issues
    </Button>
  );
  if (detail.isPending) return <LoadingState label="Loading the quality issue" />;
  if (detail.isError) {
    return (
      <RecordFailure title="Quality issue" noun="quality issue" error={detail.error} back={back} />
    );
  }
  const issue = detail.data;
  const open = issue.status === 'open';
  const canChange = open && hasCapability(session.data, 'quality.quality_issue:change');
  const canClose = open && hasCapability(session.data, 'quality.issue_verification:create');
  const close = () => {
    setDialog(null);
  };
  return (
    <Stack spacing={3}>
      <PageHeader
        title={`${issue.code} — ${issue.title}`}
        actions={
          <>
            {back}
            {canChange && (
              <Button
                variant="outlined"
                onClick={(event) => {
                  event.currentTarget.focus();
                  setDialog({ kind: 'edit' });
                }}
              >
                Edit
              </Button>
            )}
            {canClose && (
              <Button
                variant="contained"
                onClick={(event) => {
                  event.currentTarget.focus();
                  setDialog({ kind: 'close' });
                }}
              >
                Verify and close
              </Button>
            )}
          </>
        }
      />
      <Section title="Issue">
        <Facts
          label="Quality issue details"
          facts={[
            ['Status', <StatusChip key="s" status={issue.status} />],
            ['Number', issue.code],
            ['Title', issue.title],
            ['Description', issue.description],
            ['Identified on', issue.identifiedOn],
            [
              'Raised by',
              issue.raisedByLabel == null ? (
                'Not visible'
              ) : (
                <PersonName
                  key="raisedBy"
                  name={issue.raisedByLabel}
                  employeeId={issue.raisedByEmployeeId}
                />
              ),
            ],
            [
              'Owner',
              issue.ownerLabel == null ? (
                'Not visible'
              ) : (
                <PersonName
                  key="owner"
                  name={issue.ownerLabel}
                  employeeId={issue.ownerEmployeeId}
                />
              ),
            ],
            ['Category', listLabel(lists.data?.category, issue.categoryCode)],
            ['Severity', listLabel(lists.data?.severity, issue.severityCode)],
            ['Root cause', issue.rootCause ?? 'Not recorded yet'],
          ]}
        />
      </Section>
      <Section
        title="Corrective and preventive actions"
        actions={
          canChange ? (
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={(event) => {
                event.currentTarget.focus();
                setDialog({ kind: 'action' });
              }}
            >
              Add action
            </Button>
          ) : undefined
        }
      >
        {issue.actions.length === 0 ? (
          <EmptyState title="No actions recorded" />
        ) : (
          <Table size="small" aria-label="Actions">
            <TableHead>
              <TableRow>
                <TableCell>Type</TableCell>
                <TableCell>What is to be done</TableCell>
                <TableCell>Responsible</TableCell>
                <TableCell>Due</TableCell>
                <TableCell>Status</TableCell>
                {canChange && <TableCell align="right">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {issue.actions.map((action) => (
                <TableRow key={action.id} hover>
                  <TableCell>{ACTION_TYPE_LABELS[action.actionType]}</TableCell>
                  <TableCell>{action.description}</TableCell>
                  <TableCell>
                    {action.responsibleLabel == null ? (
                      'Not visible'
                    ) : (
                      <PersonName
                        name={action.responsibleLabel}
                        employeeId={action.responsibleEmployeeId}
                      />
                    )}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {action.dueDate ?? '—'}
                    {action.overdue && (
                      <Chip
                        size="small"
                        color="warning"
                        variant="outlined"
                        label="Overdue"
                        sx={{ ml: 1 }}
                      />
                    )}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <StatusChip status={action.status} />
                    {action.completedOn !== null && (
                      <Typography component="span" variant="caption" sx={{ ml: 1 }}>
                        {action.completedOn}
                      </Typography>
                    )}
                  </TableCell>
                  {canChange && (
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Button
                        size="small"
                        aria-label={`Edit action: ${action.description}`}
                        onClick={(event) => {
                          event.currentTarget.focus();
                          setDialog({ kind: 'action', action });
                        }}
                      >
                        Edit
                      </Button>
                      <Button
                        size="small"
                        aria-label={`${action.status === 'open' ? 'Mark complete' : 'Reopen'}: ${action.description}`}
                        onClick={(event) => {
                          event.currentTarget.focus();
                          setDialog({ kind: 'complete', action });
                        }}
                      >
                        {action.status === 'open' ? 'Mark complete' : 'Reopen'}
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Section>
      {issue.verification !== null && (
        <Section title="Verification and closure">
          <Facts
            label="Verification"
            facts={[
              ['Verified outcome', issue.verification.outcome],
              ['Verified on', issue.verification.verifiedOn],
              [
                'Verified by',
                issue.verification.verifiedByName == null ? (
                  'Not visible'
                ) : (
                  <PersonName
                    key="verifiedBy"
                    name={issue.verification.verifiedByName}
                    userId={issue.verification.verifiedBy}
                  />
                ),
              ],
            ]}
          />
        </Section>
      )}
      {/* Evidence of the issue, of an action or of the verified outcome (FRD-QLT-008). */}
      <DocumentsPanel
        recordType="quality.quality_issue"
        recordId={issue.id}
        canAttach={canChange}
      />
      <RecordHistory recordType="quality.quality_issue" recordId={issue.id} />
      {dialog?.kind === 'edit' && (
        <QualityIssueDialog
          key={`${issue.id}:${String(issue.version)}`}
          issue={issue}
          onClose={close}
        />
      )}
      {dialog?.kind === 'close' && <CloseDialog issue={issue} onClose={close} />}
      {dialog?.kind === 'action' && (
        <ActionDialog issue={issue} action={dialog.action} onClose={close} />
      )}
      {dialog?.kind === 'complete' && (
        <CompleteDialog issue={issue} action={dialog.action} onClose={close} />
      )}
    </Stack>
  );
}

/** Record or edit one corrective or preventive action (FRD-QLT-006). The due date is optional. */
function ActionDialog({
  issue,
  action,
  onClose,
}: {
  issue: QualityIssueDetailDto;
  action?: QualityActionDto | undefined;
  onClose: () => void;
}) {
  const session = useSession();
  const queryClient = useQueryClient();
  const canSeeEmployees = hasCapability(session.data, 'hr.employee:view');
  const creating = action === undefined;
  const [actionType, setActionType] = useState<'corrective' | 'preventive'>(
    action?.actionType ?? 'corrective',
  );
  const [description, setDescription] = useState(action?.description ?? '');
  const [responsible, setResponsible] = useState<EmployeeDto | null>(null);
  const [dueDate, setDueDate] = useState(action?.dueDate ?? '');

  const body: Record<string, string | null> = {};
  if (creating) {
    body.actionType = actionType;
    body.description = description.trim();
    if (responsible !== null) body.responsibleEmployeeId = responsible.id;
    if (dueDate !== '') body.dueDate = dueDate;
  } else {
    if (actionType !== action.actionType) body.actionType = actionType;
    if (description.trim() !== action.description) body.description = description.trim();
    if (responsible !== null && responsible.id !== action.responsibleEmployeeId) {
      body.responsibleEmployeeId = responsible.id;
    }
    if (dueDate !== (action.dueDate ?? '')) body.dueDate = dueDate || null;
  }
  const complete = description.trim() !== '' && (!creating || responsible !== null);

  const save = useMutation({
    mutationFn: () =>
      creating
        ? postJson(
            `${QUALITY_ISSUES}/${issue.id}/actions`,
            body,
            QualityIssueDetailSchema,
            session.data?.csrfToken ?? '',
          )
        : patchJson(
            `${QUALITY_ISSUES}/${issue.id}/actions/${action.id}`,
            body,
            QualityIssueDetailSchema,
            session.data?.csrfToken ?? '',
            action.version,
          ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['quality'] });
      await queryClient.invalidateQueries({ queryKey: ['platform', 'audit'] });
      onClose();
    },
  });

  return (
    <FormDialog
      open
      title={creating ? 'Add action' : 'Edit action'}
      submitLabel={creating ? 'Add action' : 'Save changes'}
      canSubmit={complete && Object.keys(body).length > 0}
      submitting={save.isPending}
      error={save.isError ? save.error.message : null}
      onClose={onClose}
      onSubmit={() => {
        save.mutate();
      }}
    >
      <TextField
        select
        label="Type"
        required
        value={actionType}
        onChange={(e) => {
          setActionType(e.target.value === 'preventive' ? 'preventive' : 'corrective');
        }}
        helperText="Corrective fixes this issue; preventive stops it happening again."
      >
        <MenuItem value="corrective">Corrective</MenuItem>
        <MenuItem value="preventive">Preventive</MenuItem>
      </TextField>
      <TextField
        label="What is to be done"
        required
        multiline
        minRows={2}
        value={description}
        onChange={(e) => {
          setDescription(e.target.value);
        }}
      />
      {canSeeEmployees ? (
        <ActiveEmployeePicker
          label={creating ? 'Responsible' : 'Change responsible'}
          required={creating}
          value={responsible}
          onChange={setResponsible}
          helperText={
            creating
              ? 'The employee who is to carry the action out.'
              : `Currently ${action.responsibleLabel ?? 'not visible'}`
          }
        />
      ) : (
        <Alert severity="warning" variant="outlined">
          Choosing the responsible employee needs permission to view employees.
        </Alert>
      )}
      <TextField
        type="date"
        label="Due date"
        value={dueDate}
        slotProps={{ inputLabel: { shrink: true } }}
        onChange={(e) => {
          setDueDate(e.target.value);
        }}
        helperText="Optional."
      />
    </FormDialog>
  );
}

/** Marks an action complete, or withdraws its completion, after a confirmation. */
function CompleteDialog({
  issue,
  action,
  onClose,
}: {
  issue: QualityIssueDetailDto;
  action: QualityActionDto;
  onClose: () => void;
}) {
  const session = useSession();
  const queryClient = useQueryClient();
  const completing = action.status === 'open';
  const save = useMutation({
    mutationFn: () =>
      patchJson(
        `${QUALITY_ISSUES}/${issue.id}/actions/${action.id}`,
        { completed: completing },
        QualityIssueDetailSchema,
        session.data?.csrfToken ?? '',
        action.version,
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['quality'] });
      await queryClient.invalidateQueries({ queryKey: ['platform', 'audit'] });
      onClose();
    },
  });
  return (
    <ConfirmDialog
      title={completing ? 'Mark action complete' : 'Reopen action'}
      message={
        completing
          ? `Mark "${action.description}" as complete? Today is recorded as its completion date.`
          : `Reopen "${action.description}"? Its completion date is removed, and the issue cannot be closed until it is complete again.`
      }
      confirmLabel={completing ? 'Mark complete' : 'Reopen'}
      pending={save.isPending}
      error={save.isError ? save.error.message : null}
      onConfirm={() => {
        save.mutate();
      }}
      onClose={onClose}
    />
  );
}

/**
 * Verify and close (FRD-QLT-007): the verified outcome is recorded with the verifier and today's
 * date. Refused by the API while an action is open; closing cannot be undone.
 */
function CloseDialog({ issue, onClose }: { issue: QualityIssueDetailDto; onClose: () => void }) {
  const session = useSession();
  const queryClient = useQueryClient();
  const [outcome, setOutcome] = useState('');
  const save = useMutation({
    mutationFn: () =>
      postJson(
        `${QUALITY_ISSUES}/${issue.id}/close`,
        { outcome: outcome.trim() },
        QualityIssueDetailSchema,
        session.data?.csrfToken ?? '',
        { expectedVersion: issue.version },
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['quality'] });
      await queryClient.invalidateQueries({ queryKey: ['platform', 'audit'] });
      onClose();
    },
  });
  const blocked = issue.openActionCount > 0;
  return (
    <FormDialog
      open
      title={`Verify and close ${issue.code}`}
      submitLabel="Verify and close"
      canSubmit={!blocked && outcome.trim() !== ''}
      submitting={save.isPending}
      error={save.isError ? save.error.message : null}
      onClose={onClose}
      onSubmit={() => {
        save.mutate();
      }}
    >
      {blocked ? (
        <Alert severity="warning" variant="outlined">
          {String(issue.openActionCount)} action(s) are not complete. Complete them before closing.
        </Alert>
      ) : (
        <Alert severity="info" variant="outlined">
          Closing cannot be undone: the issue and its actions can no longer be changed.
        </Alert>
      )}
      <TextField
        label="Verified outcome"
        required
        autoFocus
        multiline
        minRows={3}
        disabled={blocked}
        value={outcome}
        onChange={(e) => {
          setOutcome(e.target.value);
        }}
        helperText="What was checked, and what confirms the issue is resolved."
      />
    </FormDialog>
  );
}
